<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import { ref, useTemplateRef, watch } from 'vue';

import DialogModal from '@/components/dialogs/DialogModal.vue';

const isOpen = defineModel<boolean>('open', { default: false });
const dialogModalComponent = useTemplateRef<InstanceType<typeof DialogModal>>('dialogElement');
const actionsSentinel = useTemplateRef<HTMLElement>('actionsSentinel');

const _props = defineProps<{
  hideCloseButton?: boolean;
  showFooter?: boolean;
}>();

const emit = defineEmits<{
  'dialog-close-transition-ended': [];
}>();

const actionsAreFixed = ref(false);
const observerEntries = ref<IntersectionObserverEntry[]>([]);

useIntersectionObserver(
  actionsSentinel,
  observerCallback,
  {
    rootMargin: '0px 0px 0px 0px',
    threshold: 1
  }
);

function observerCallback(entries: IntersectionObserverEntry[], _observerElement?: IntersectionObserver) {
  observerEntries.value = entries;
  const entry = entries[0];
  actionsAreFixed.value = !entry.isIntersecting;
}

defineExpose({
  dialogModalComponent
});

watch(isOpen, (newValue: boolean) => {
  if(!dialogModalComponent.value) {
    return;
  }

  if(newValue) {
    requestAnimationFrame(() => {
      console.log(observerEntries.value);
      // L'observer vérifiera automatiquement lors de la prochaine frame
      // Vous pouvez éventuellement forcer une relecture
      void actionsSentinel.value?.getBoundingClientRect();
    });
  }

  if (!newValue && dialogModalComponent.value) {
    const nativeDialog = dialogModalComponent.value.dialogElement;
    const isFirefox = navigator.userAgent.includes('Firefox');

    if (nativeDialog && !isFirefox) {
      nativeDialog.addEventListener('transitionend', function resetDialogUnit(_event: TransitionEvent) {
        emit('dialog-close-transition-ended');
      }, { once: true });
    }
  }
});
</script>

<template>
  <DialogModal ref="dialogModalComponent"
               v-model:open="isOpen"
               :hide-close-button="hideCloseButton"
               class="canvasDialogModal">
    <template #container>
      <div class="canvasDialogModal-content">
        <div class="canvasDialogModal-image">
          <slot name="image"></slot>
        </div>
        <div class="canvasDialogModal-dataPanel">
          <div class="canvasDialogModal-description">
            <slot name="description"></slot>
            <a ref="actionsSentinel"
               class="sentinel"></a>
          </div>
          <div class="canvasDialogModal-actions"
               :class="{
                 'canvasDialogModal-actions_fixed': actionsAreFixed
               }">
            <slot name="actions"></slot>
          </div>
        </div>
      </div>
    </template>

    <!-- <template #header="{ bodyIsScrolledToTop }">
      <slot name="header"
            v-bind="{ bodyIsScrolledToTop }"></slot>
    </template>
    
    <div class="canvasDialogModal-contentContainer">
      <div class="canvasDialogModal-content"
           :data-availability-status="$attrs['data-availability-status']">
        <div class="canvasDialogModal-image">
          <slot name="image"></slot>
        </div>
        <div class="canvasDialogModal-description">
          <slot name="description"></slot>
          <a ref="actionsSentinel"
             class="sentinel"></a>
        </div>
        <div class="canvasDialogModal-actions"
             :class="{
               'canvasDialogModal-actions_fixed': actionsAreFixed
             }">
          <slot name="actions"></slot>
        </div>
      </div>
    </div>
    
    <template #footer>
      <slot name="footer"></slot>
    </template>
    
    <template #headerContent>
      <slot name="headerContent"></slot>
    </template>
    
    <template #footerContent>
      <slot name="footerContent">
      </slot>
    </template> -->
  </DialogModal>
</template>

<style lang="scss">
.canvasDialogModal {
  width: min(106ch, 100% - (2 * 1rem));

  hgroup {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1.0rem;
    margin-bottom:1.0rem;

    h3{
      font-family: Inter;
      font-size: var(--fs24-20);
      max-width: 60%;
      margin:0;
    }
  }
}
.canvasDialogModal-content{
  display:flex;
  flex-direction: row;
  align-items: stretch;

  .canvasDialogModal-dataPanel {
    display:flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1.6rem;
    padding:6.0rem;
    width:50%;
  }

  .canvasDialogModal-image {
    width:50%;
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.5);
      border-radius: 4px;
      
      &:hover {
        background: rgba(255, 255, 255, 0.7);
      }
    }

    img {
      width: 100%;
      height: 100%;
      object-position: center;
      object-fit: contain;
      flex-shrink: 0;
    }
  }
}

.canvasDialogModal-actions {
  .btn{
    width: 100%;
  }
}

.canvasDialogModal-descriptionList {
 display:flex;
 gap:1.0rem;
 flex-wrap: wrap;
 justify-content: space-between;
}

.canvasDialogModal-descriptionItem {
  width:calc(50% - 0.5rem);
  font-size:1.4rem;

  &_fullWidth {
    width:100%
  }

  dt {
    color: var(--smallHeadingColor);
  }

  dd {
    margin-top: 0;
    font-weight: 700;
  }
}
@media screen and (max-width:1023px) {
  .canvasDialogModal{
    max-width:500px;
    min-width:auto;
  }
  .canvasDialogModal-content{
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .canvasDialogModal-image{
      width:100%;
      max-height: 100%;
      height: 40vh;
      
      img {
        object-fit: contain;
      }
    }

    .canvasDialogModal-dataPanel{
      width:100%;
      padding:2.4rem;
      gap:var(--spc24-16);
    }
  }

  .canvasDialogModal-descriptionList{
    flex-direction: row;
    gap:1.0rem;
  }
  .canvasDialogModal-descriptionItem{
    width:calc(50% - 0.8rem);
    font-size:1.4rem;
  }
}

</style>
